/**
 * Insights data fetch hook
 *
 * Provides AI-powered insights (trends, patterns, correlations) using TanStack Query
 * with proper caching, loading states, and error handling.
 *
 * Per spec 10, insights include:
 * - Trend observations (pain increasing/decreasing)
 * - Pattern identification (day-of-week patterns, location patterns)
 * - Correlation observations (observational only, no causation)
 * - Progress indicators (logging streaks, frequency)
 * - Focus suggestions (prompts to track additional data)
 */

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  createApiClient,
  ApiClientError,
  type InsightsResponse,
  type InsightsDateRange,
} from "@floline/api-client";
import { API_URL } from "@/env";
import { i18n } from "@/i18n";
import {
  mapConsentRequiredError,
  isConsentRequiredClientError,
} from "@/lib/consent-errors";

/** Query key factory for insights */
export const insightsKeys = {
  all: ["insights"] as const,
  byDateRange: (dateRange: InsightsDateRange, language: "en" | "fi") =>
    [...insightsKeys.all, dateRange, language] as const,
};

/** API client configured for insights requests */
const apiClient = createApiClient(
  API_URL
);

/**
 * Fetch insights from the API
 *
 * @param dateRange - Date range for analysis (default: "30d")
 * @returns Insights response with generated insights and metadata
 */
async function fetchInsights(
  dateRange: InsightsDateRange = "30d",
  language: "en" | "fi" = "en"
): Promise<InsightsResponse> {
  try {
    return await apiClient.getInsights({ dateRange, language });
  } catch (error: unknown) {
    const consentError = mapConsentRequiredError(error, i18n.t.bind(i18n));
    if (consentError) {
      throw consentError;
    }
    throw error;
  }
}

/**
 * Options for useInsights hook
 */
export interface UseInsightsOptions {
  /** Date range for analysis (default: "30d") */
  dateRange?: InsightsDateRange;
  /** Whether the query is enabled (default: true) */
  enabled?: boolean;
}

/**
 * Hook to fetch AI-powered insights from pain data
 *
 * Returns insights along with loading and error states.
 * Insights are cached for 5 minutes as they don't change frequently.
 *
 * @example
 * ```tsx
 * function InsightsPage() {
 *   const { insights, meta, isLoading, error, refetch } = useInsights({
 *     dateRange: "30d",
 *   });
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <ErrorMessage />;
 *
 *   return (
 *     <div>
 *       {insights.map(insight => (
 *         <InsightCard key={insight.id} insight={insight} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useInsights(options: UseInsightsOptions = {}) {
  const { dateRange = "30d", enabled = true } = options;
  const { i18n } = useTranslation();
  const language: "en" | "fi" =
    i18n.resolvedLanguage === "fi" || i18n.language.startsWith("fi") ? "fi" : "en";

  const query = useQuery({
    queryKey: insightsKeys.byDateRange(dateRange, language),
    queryFn: () => fetchInsights(dateRange, language),
    enabled,
    // Insights can be cached longer - they're computed from historical data
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Cache for 30 minutes even when unused
    gcTime: 30 * 60 * 1000,
    // Retry on errors (except auth errors)
    retry: (failureCount: number, error: Error) => {
      if (error instanceof ApiClientError && error.status === 401) {
        return false;
      }
      if (isConsentRequiredClientError(error)) {
        return false;
      }
      return failureCount < 3;
    },
    // Refetch on mount if stale
    refetchOnMount: true,
  });

  return {
    /** Array of generated insights */
    insights: query.data?.data.insights ?? [],
    /** Insights result with metadata */
    data: query.data?.data ?? null,
    /** Response metadata (date range, counts, timestamp) */
    meta: query.data?.meta ?? null,
    /** Whether there are new (non-dismissed) insights available */
    hasNewInsights: query.data?.meta?.hasNewInsights ?? false,
    /** Count of new (non-dismissed) insights */
    newInsightsCount: query.data?.meta?.newInsightsCount ?? 0,
    /** Whether insights are currently being fetched */
    isLoading: query.isLoading,
    /** Whether the initial fetch is in progress */
    isPending: query.isPending,
    /** Whether the fetch failed with an error */
    isError: query.isError,
    /** Error details if the fetch failed */
    error: query.error,
    /** Refetch the insights manually */
    refetch: query.refetch,
  };
}
